import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { startOfDay, subMinutes, endOfDay, fromUnixTime } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { task } from 'ember-concurrency';
import type GenericResourceModel from 'garaje/models/generic-resource';
import { createURN, getIdFromURN } from 'garaje/models/generic-resource';
import type MapFeatureModel from 'garaje/models/map-feature';
import type MapFloorModel from 'garaje/models/map-floor';
import type AjaxService from 'garaje/services/ajax';
import { isFetchError } from 'garaje/services/ajax/errors';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LiveMapService from 'garaje/services/live-map';
import { AvailabilityStatus, type ResourceState, type ResourceType } from 'garaje/services/live-map';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';

interface ResourcePopupComponentArgs {
  selectedDate: Date;
  selectedTime?: number;
  selectedEndTime?: number;
  feature: MapFeatureModel;
  mapFloor: MapFloorModel;
  currentUserReservations: ResourceState[];
  onCancel: () => void;
}

export interface CreateReservationPayload {
  resourceId: string;
  relationId: string;
  startTime: string;
  endTime: string;
  reservedFor: string;
  reservedBy: string;
  bookingSource: 'web_app';
}

interface UpdateReservationPayload {
  bookingSource: 'web_app';
  resourceId: string;
}

interface ReservationTimeResponse {
  data: {
    startTime: string;
    endTime: string;
  };
}

export default class ResourcePopupComponent extends Component<ResourcePopupComponentArgs> {
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare ajax: AjaxService;
  @service declare flashMessages: FlashMessagesService;
  @service declare liveMap: LiveMapService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare metrics: MetricsService;

  @tracked resource: GenericResourceModel | undefined;
  @tracked resourceState: ResourceState | undefined;
  @tracked resourceAvailability: AvailabilityStatus | undefined;
  @tracked reservationStartTime: string | undefined;
  @tracked reservationEndTime: string | undefined;
  @tracked isLoadingReservationTimes: boolean = true;

  constructor(owner: unknown, args: ResourcePopupComponentArgs) {
    super(owner, args);
    void this.loadResourceAndStateTask.perform();
    this.isLoadingReservationTimes = this.loadResourceAndStateTask.isRunning;
  }

  loadResourceAndStateTask = task({ drop: true }, async (): Promise<void> => {
    const resourceId = this.args.feature.externalId;
    this.resource = await this.store.findRecord('generic-resource', resourceId);
    this.resourceState = this.liveMap.getResourceStateByIdAndType(resourceId, this.args.feature.type as ResourceType);
    this.resourceAvailability = this.resourceState?.status.availabilityStatus;
    if (
      this.resourceAvailability === AvailabilityStatus.OCCUPIED ||
      this.resourceAvailability === AvailabilityStatus.UNAVAILABLE
    ) {
      // make API call here
      const reservationId = this.resourceState?.status.reservationId;
      if (reservationId) {
        const reservation = await this.ajax.request<ReservationTimeResponse>(
          `/a/rms/v2/generics/reservations/${reservationId}`,
          { type: 'GET' },
        );
        this.reservationStartTime = reservation.data.startTime;
        this.reservationEndTime = reservation.data.endTime;
      }
      this.isLoadingReservationTimes = false;
    }
  });

  get hasAssignment(): boolean {
    return this.resourceState?.assignedTo != null;
  }

  get isAssignedToCurrentUser(): boolean {
    return getIdFromURN(this.resourceState?.assignedTo) === this.state.currentAdmin.employee?.id;
  }

  get isReservedByCurrentUser(): boolean {
    return getIdFromURN(this.resourceState?.status.reservedFor) === this.state.currentAdmin.employee?.id;
  }

  get currentUserRelevantReservations(): ResourceState[] {
    return this.args.currentUserReservations.filter(
      (resourceState) => resourceState.resourceType === (this.args.feature.type as ResourceType),
    );
  }

  get resourceTypeLabel(): string {
    // TODO: we will need to fetch all the different resource types and their labels
    // once the API is ready
    switch (this.resource?.resourceTypeId) {
      case 2:
        return 'Parking';
      default:
        return 'Resource';
    }
  }

  replaceExistingReservationTask = task({ drop: true }, async () => {
    let payload: (UpdateReservationPayload & { startTime: string; endTime: string }) | undefined;
    try {
      if (!this.currentUserRelevantReservations) {
        throw new Error('Current user has no reservations');
      }

      const existingReservationState = this.currentUserRelevantReservations[0];
      if (!existingReservationState) {
        throw new Error('Cannot find existing reservation');
      }

      if (!this.resource) {
        throw new Error('Cannot find resource to reserve');
      }

      // cancel the existing reservation and release the tied resource
      // and book the new resource that is being selected
      const existingReservationId = existingReservationState.status.reservationId;

      // Compute start/end using selectedTime if provided; otherwise fall back to full day
      const locationStart =
        this.args.selectedTime != null
          ? this.state.getOfficeLocationTime(fromUnixTime(this.args.selectedTime))
          : this.state.getOfficeLocationTime(
              startOfDay(
                subMinutes(this.args.selectedDate, this.state.minutesBetweenTimezones(this.args.selectedDate)),
              ),
            );
      const locationEnd =
        this.args.selectedEndTime != null
          ? this.state.getOfficeLocationTime(fromUnixTime(this.args.selectedEndTime))
          : this.state.getOfficeLocationTime(
              endOfDay(toZonedTime(this.args.selectedDate, this.state.currentLocation.timezone)),
            );

      payload = {
        bookingSource: 'web_app',
        resourceId: this.resource.id,
        startTime: locationStart.toISOString(),
        endTime: locationEnd.toISOString(),
      };

      await this.ajax.request(`/a/rms/v2/generics/reservations/${existingReservationId}/change`, {
        type: 'POST',
        contentType: 'application/vnd.api+json',
        data: { data: payload },
      });

      await this.liveMap.restartPollingLiveMap();
      void this.loadResourceAndStateTask.perform();
      this.flashMessages.showAndHideFlash('success', 'Reservation successfully changed');
      this.args.onCancel();
    } catch (error) {
      const errorText = parseErrorForDisplay(error);
      const debugExtras: Record<string, unknown> = {
        action: 'change',
        locationId: this.state.currentLocation.id,
        mapFloorId: this.args.mapFloor.id,
        featureExternalId: this.args.feature.externalId,
        reservationId: this.currentUserRelevantReservations[0]?.status.reservationId,
        resourceTypeId: this.resource?.resourceTypeId,
      };
      if (payload) {
        debugExtras['requestPayload'] = payload;
      }
      if (error instanceof Error && isFetchError(error)) {
        debugExtras['httpStatus'] = error.status;
        debugExtras['apiResponsePayload'] = error.payload;
      }
      this.metrics.logMonitorError({
        event: 'LIVE_MAP_GENERIC_RESERVATION_API_FAILED',
        debugExtras,
        error,
      });
      this.flashMessages.showAndHideFlash('error', errorText);
    }
  });

  createResourceReservationTask = task({ drop: true }, async () => {
    let payload: CreateReservationPayload | undefined;
    try {
      const currentEmployee = this.currentAdmin.employee;

      // The current employee viewing map can only book resources for themselves
      if (!currentEmployee) {
        throw new Error('Cannot find current employee');
      }

      if (!this.resource) {
        throw new Error('Cannot find resource to reserve');
      }

      const locationTime =
        this.args.selectedTime != null
          ? this.state.getOfficeLocationTime(fromUnixTime(this.args.selectedTime))
          : this.state.getOfficeLocationTime(
              startOfDay(
                subMinutes(this.args.selectedDate, this.state.minutesBetweenTimezones(this.args.selectedDate)),
              ),
            );
      const locationEndTime =
        this.args.selectedEndTime != null
          ? this.state.getOfficeLocationTime(fromUnixTime(this.args.selectedEndTime))
          : this.state.getOfficeLocationTime(
              endOfDay(toZonedTime(this.args.selectedDate, this.state.currentLocation.timezone)),
            );

      payload = {
        resourceId: this.resource?.id,
        relationId: createURN('location', this.state.currentLocation.id),
        startTime: locationTime.toISOString(),
        endTime: locationEndTime.toISOString(),
        reservedFor: createURN('employee', currentEmployee?.id),
        reservedBy: createURN('employee', currentEmployee?.id),
        bookingSource: 'web_app',
      };

      await this.ajax.request(`/a/rms/v2/generics/reservations`, {
        type: 'POST',
        contentType: 'application/vnd.api+json',
        data: { data: payload },
      });

      this.metrics.trackEvent('Created Resource Reservation', {
        ...payload,
        resourceTypeId: this.resource?.resourceTypeId,
      });

      await this.liveMap.restartPollingLiveMap();
      void this.loadResourceAndStateTask.perform();
      this.flashMessages.showAndHideFlash('success', 'Reservation successfully created');
      this.args.onCancel();
    } catch (error) {
      const errorText = parseErrorForDisplay(error);
      const debugExtras: Record<string, unknown> = {
        action: 'create',
        locationId: this.state.currentLocation.id,
        mapFloorId: this.args.mapFloor.id,
        featureExternalId: this.args.feature.externalId,
        resourceTypeId: this.resource?.resourceTypeId,
        employeeId: this.currentAdmin.employee?.id,
      };
      if (payload) {
        debugExtras['requestPayload'] = payload;
      }
      if (error instanceof Error && isFetchError(error)) {
        debugExtras['httpStatus'] = error.status;
        debugExtras['apiResponsePayload'] = error.payload;
      }
      this.metrics.logMonitorError({
        event: 'LIVE_MAP_GENERIC_RESERVATION_API_FAILED',
        debugExtras,
        error,
      });
      this.flashMessages.showAndHideFlash('error', errorText);
    }
  });

  releaseResourceReservationTask = task({ drop: true }, async () => {
    try {
      const reservationId = this.resourceState?.status.reservationId;
      if (!reservationId) throw new Error('Cannot find reservation id');

      await this.ajax.request(`/a/rms/v2/generics/reservations/${reservationId}`, {
        type: 'DELETE',
        contentType: 'application/vnd.api+json',
      });

      this.metrics.trackEvent('Released Resource Reservation', {
        reservationId,
        resourceId: this.args.feature.externalId,
        resourceTypeId: this.resource?.resourceTypeId,
        employeeId: this?.currentAdmin?.employee?.id,
      });

      await this.liveMap.restartPollingLiveMap();
      void this.loadResourceAndStateTask.perform();
      this.flashMessages.showAndHideFlash('success', 'Reservation cancelled');
      this.args.onCancel();
    } catch (error) {
      const errorText = parseErrorForDisplay(error);
      this.flashMessages.showAndHideFlash('error', errorText);
    }
  });
}
